export type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>

/**
 * Run a fetch with a hard deadline and propagate an existing caller signal.
 * The explicit Promise race keeps the caller bounded even if a test double or
 * non-standard fetch implementation fails to reject when aborted.
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = 10_000,
  fetcher: FetchLike = fetch
): Promise<Response> {
  if (!Number.isSafeInteger(timeoutMs) || timeoutMs < 1) {
    throw new Error('Fetch timeout must be a positive integer')
  }

  const controller = new AbortController()
  const parentSignal = init.signal
  const abortFromParent = () => controller.abort(parentSignal?.reason)
  if (parentSignal?.aborted) abortFromParent()
  else parentSignal?.addEventListener('abort', abortFromParent, { once: true })

  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort()
      reject(new Error(`Request timed out after ${timeoutMs}ms`))
    }, timeoutMs)
  })

  try {
    return await Promise.race([
      fetcher(input, { ...init, signal: controller.signal }),
      deadline,
    ])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
    parentSignal?.removeEventListener('abort', abortFromParent)
  }
}
